import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Introduction",
      content: `BodyCorp365 is committed to protecting the privacy and personal information of users who access and use the BodyCorp365 platform.

This Privacy Policy explains how we collect, use, store, and protect personal information when individuals use the BodyCorp365 website, mobile application, and related services.

By using the BodyCorp365 platform, you consent to the collection and use of your information in accordance with this Privacy Policy.`
    },
    {
      title: "2. Information We Collect",
      content: `BodyCorp365 may collect the following types of information when users register or interact with the platform:`,
      subsections: [
        {
          subtitle: "Personal Information",
          items: [
            "Name and surname",
            "Email address",
            "Phone number",
            "Unit or property details",
            "User account login credentials"
          ]
        },
        {
          subtitle: "Complex or Property Information",
          description: "Information relating to residential complexes may include:",
          items: [
            "Building or scheme name",
            "Unit numbers",
            "Maintenance requests",
            "Notices and communications",
            "Uploaded documents"
          ]
        },
        {
          subtitle: "Technical Information",
          description: "We may also collect limited technical information such as:",
          items: [
            "Device type",
            "IP address",
            "Browser type",
            "Login activity"
          ],
          note: "This information helps us maintain system security and improve platform performance."
        }
      ]
    },
    {
      title: "3. How We Use Information",
      content: "BodyCorp365 uses collected information to:",
      items: [
        "Provide access to the platform and its services",
        "Facilitate communication between residents, trustees, and managing agents",
        "Manage maintenance requests and contractor interactions",
        "Store and organise documents related to residential complexes",
        "Improve platform functionality and user experience",
        "Ensure the security and integrity of the system"
      ],
      note: "We only process information necessary for the proper operation of the platform."
    },
    {
      title: "4. Information Sharing",
      content: `BodyCorp365 does not sell or rent personal information to third parties.

Information may be shared only in the following circumstances:`,
      items: [
        "With trustees or managing agents responsible for managing a residential complex",
        "With contractors when responding to approved maintenance requests",
        "With service providers who assist in hosting and maintaining the platform",
        "Where required by law or regulatory authorities"
      ],
      note: "All third parties involved are required to respect the confidentiality and security of personal information."
    },
    {
      title: "5. Data Security",
      content: "BodyCorp365 takes reasonable technical and organisational measures to protect personal information from:",
      items: [
        "Unauthorised access",
        "Loss or misuse",
        "Alteration or destruction"
      ],
      note: "Access to information is restricted to authorised users and administrators responsible for managing the platform."
    },
    {
      title: "6. Data Storage",
      content: "Information provided to BodyCorp365 is stored on secure servers and retained only for as long as necessary to provide platform services and maintain operational records for residential complexes."
    },
    {
      title: "7. User Rights",
      content: "Users have the right to:",
      items: [
        "Access the personal information held about them",
        "Request correction of inaccurate information",
        "Request deletion of their account information where applicable"
      ],
      note: "Requests may be submitted to BodyCorp365 through the contact details provided below."
    },
    {
      title: "8. Cookies and Platform Usage",
      content: `The BodyCorp365 website may use basic cookies or similar technologies to improve website functionality and analyse usage patterns.

Users may disable cookies in their browser settings if preferred.`
    },
    {
      title: "9. Changes to this Policy",
      content: `BodyCorp365 may update this Privacy Policy from time to time to reflect changes in legal requirements or improvements to the platform.

Users will be notified of material changes through the website or application.`
    },
    {
      title: "10. Contact Information",
      content: "For questions regarding this Privacy Policy or the handling of personal information, please contact:",
      contact: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="BodyCorp365" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold font-manrope text-slate-900">BodyCorp365</span>
            </div>
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-manrope text-slate-900">Privacy Policy</h1>
              <p className="text-slate-500">Last Updated: 14 March 2026</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-8 sm:p-12 space-y-10">
            {sections.map((section, index) => (
              <section key={index} className="scroll-mt-20" id={`section-${index + 1}`}>
                <h2 className="text-xl font-bold text-slate-900 mb-4">{section.title}</h2>
                
                {section.content && (
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line mb-4">
                    {section.content}
                  </p>
                )}

                {section.subsections && (
                  <div className="space-y-6 ml-4">
                    {section.subsections.map((sub, subIndex) => (
                      <div key={subIndex}>
                        <h3 className="font-semibold text-slate-800 mb-2">{sub.subtitle}</h3>
                        {sub.description && (
                          <p className="text-slate-600 mb-2">{sub.description}</p>
                        )}
                        {sub.items && (
                          <ul className="list-disc list-inside space-y-1 text-slate-600 ml-2">
                            {sub.items.map((item, itemIndex) => (
                              <li key={itemIndex}>{item}</li>
                            ))}
                          </ul>
                        )}
                        {sub.note && (
                          <p className="text-slate-500 text-sm mt-2 italic">{sub.note}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {section.items && !section.subsections && (
                  <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}

                {section.note && (
                  <p className="text-slate-500 text-sm mt-4 italic">{section.note}</p>
                )}

                {section.contact && (
                  <div className="bg-slate-50 rounded-xl p-6 mt-4">
                    <h3 className="font-semibold text-slate-800 mb-3">BodyCorp365 Support</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-blue-600" />
                        <a href="mailto:hello@bodycorp365.com" className="text-blue-600 hover:underline">
                          hello@bodycorp365.com
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <span className="text-slate-500">Website:</span>
                        <a href="https://www.bodycorp365.com" className="text-blue-600 hover:underline">
                          www.bodycorp365.com
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <Button variant="outline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            Back to Top
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} BodyCorp365. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
